import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const EMAIL = "hei@clientflow.no";

/**
 * Visible, copyable contact e-mail for footers.
 * - Clicking the address opens the user's mail app (mailto).
 * - The copy button writes the address to the clipboard with a toast confirmation,
 *   so the action still works even if no mail client is configured.
 */
export default function FooterContact({ className = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    // Don't trigger the surrounding mailto when the copy icon is clicked.
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(EMAIL);
      } else {
        // Fallback for older mobile browsers
        const ta = document.createElement("textarea");
        ta.value = EMAIL;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("E-post kopiert", { description: EMAIL });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Klarte ikke å kopiere", { description: "Prøv å trykke og hold på e-posten." });
    }
  };

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <a
        href={`mailto:${EMAIL}`}
        className="underline-offset-4 hover:text-foreground hover:underline focus-visible:underline focus-visible:outline-none active:opacity-80 transition-colors"
        data-testid="footer-email-link"
        aria-label={`Send e-post til ${EMAIL}`}
      >
        {EMAIL}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:scale-95 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        aria-label="Kopier e-postadresse"
        title="Kopier e-post"
        data-testid="footer-email-copy"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-foreground" strokeWidth={2} />
        ) : (
          <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
        )}
      </button>
    </span>
  );
}
